import React, { useEffect } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useLocation } from 'react-router-dom';
import { parse } from 'query-string';

import {
	approvalChallengeState,
	tokensState,
	approvalVerifierState,
} from '../../features/auth';
import { TokensDto } from '@oh-auth/dto';
import makeId from '../../helpers/makeId';
import { configState } from '../../features/config';
import { Consent } from './Consent';
import { Authorize } from './Authorize';

interface ApprovalProps {
	approve_uri: string;
	consent_uri?: string;
}

export function Approval(props: ApprovalProps) {
	const { approve_uri, consent_uri } = props;
	const { sub } = useRecoilValue(tokensState) as TokensDto;
	const { search } = useLocation();
	const { client_id } = parse(search) as any;

	let [approval_challenge, setChallenge] = useRecoilState(
		approvalChallengeState
	);
	const { APPROVAL_CHALLENGE_BYTES } = useRecoilValue(configState);
	let [approval_verifier, setVerifier] = useRecoilState(
		approvalVerifierState
	);
	let [approval_verifier_expired_at, setVerifierExpiredAt] = useRecoilState(
		approvalVerifierState
	);

	useEffect(() => {
		if (!approval_challenge) {
			let new_approval_challenge = makeId(APPROVAL_CHALLENGE_BYTES);
			sessionStorage.setItem(
				'approval_challenge',
				new_approval_challenge
			);
			setChallenge(new_approval_challenge);
		}
	}, [approval_challenge, APPROVAL_CHALLENGE_BYTES, setChallenge]);

	useEffect(() => {
		async function approve(): Promise<{
			approval_verifier: string;
			approval_verifier_expired_at: string;
		}> {
			const response = await fetch(approve_uri, {
				method: 'POST',
				headers: {
					'Content-type': 'application/json',
				},
				body: JSON.stringify({
					approval_challenge,
					sub,
					client_id,
				}),
			});

			return await response.json();
		}

		if (!approval_verifier) {
			approve().then((result) => {
				let {
					approval_verifier,
					approval_verifier_expired_at,
				} = result;
				sessionStorage.setItem('approval_verifier', approval_verifier);
				sessionStorage.setItem(
					'approval_verifier_expired_at',
					approval_verifier_expired_at
				);
				setVerifier(approval_verifier);
				setVerifierExpiredAt(approval_verifier_expired_at);
			});
		}
	}, [
		approval_challenge,
		approval_verifier,
		approve_uri,
		client_id,
		setVerifier,
		setVerifierExpiredAt,
		sub,
	]);

	return approval_verifier &&
		approval_verifier_expired_at &&
		approval_challenge ? (
		consent_uri ? (
			<Consent
				consent_uri={consent_uri}
				approval_verifier={approval_verifier}
				approval_verifier_expired_at={approval_verifier_expired_at}
				approval_challenge={approval_challenge}
			/>
		) : (
			<Authorize
				approval_verifier={approval_verifier}
				approval_verifier_expired_at={approval_verifier_expired_at}
				approval_challenge={approval_challenge}
			/>
		)
	) : null;
}
